import React, {
  createContext,
  useContext,
  FC,
  useState,
  useEffect,
} from "react";

import { LocationDto } from "@apiModels/LocationDto.csharp";
import { SpecializationDto } from "@apiModels/SpecializationDto.csharp";
import { UrlHandler } from "../UrlHandler";
import { InsuranceServiceDto } from "@apiModels/Insurance/InsuranceServiceDto.csharp";
import { InsuranceDoctorDto } from "@apiModels/Insurance/InsuranceDoctorDto.csharp";
import { InsuranceKeyword } from "@apiModels/Insurance/InsuranceKeyword.csharp";

type FiltersContext = {
  specialization: SpecializationDto | null;
  service: InsuranceServiceDto | null;
  locations: LocationDto[] | null;
  doctor: InsuranceDoctorDto | null;
  keywords: InsuranceKeyword[] | null;
  setSpecialization: (value: SpecializationDto | null) => void;
  setService: (value: InsuranceServiceDto | null) => void;
  setLocations: (value: LocationDto[] | null) => void;
  setDoctor: (value: InsuranceDoctorDto | null) => void;
  setKeywords: (value: InsuranceKeyword[] | null) => void;
  semaphore?: boolean;
};

const Context = createContext<FiltersContext | null>(null);

export const InsuranceFiltersContext: FC = ({ children }) => {
  const [specialization, setSpecialization] =
    useState<SpecializationDto | null>(null);
  const [service, setService] = useState<InsuranceServiceDto | null>(null);
  const [locations, setLocations] = useState<LocationDto[] | null>(null);
  const [doctor, setDoctor] = useState<InsuranceDoctorDto | null>(null);
  const [keywords, setKeywords] = useState<InsuranceKeyword[] | null>(null);
  const [semaphore, setSemaphore] = useState(false);

  const initialValue: FiltersContext = {
    specialization,
    service,
    locations,
    doctor,
    keywords,
    setSpecialization,
    setService,
    setLocations,
    setDoctor,
    setKeywords,
    semaphore,
  };

  useEffect(() => {
    UrlHandler.adjustActiveUrl({
      specialization,
      service,
      locations,
      doctor,
    });
  }, [specialization, service, locations, doctor]);

  if (locations !== null && semaphore == false) {
    // value change after the URL filters have done parsing
    setSemaphore(true);
  }

  return <Context.Provider value={initialValue}>{children}</Context.Provider>;
};

export const useInsuranceFilters = () => {
  const context = useContext(Context);
  if (!context) {
    throw new Error("Context has not been initialized");
  }

  return context;
};
